import React, { useState } from 'react';
import FormControl from '@material-ui/core/FormControl';
import Autocomplete from '@material-ui/lab/Autocomplete';
import { useApi } from '@backstage/core-plugin-api';
import { catalogApiRef } from '@backstage/plugin-catalog-react';
import { useAsync } from 'react-use';
import { EntityFilterQuery } from '@backstage/catalog-client';
import { TextField } from '@material-ui/core';
import { Entity } from '@backstage/catalog-model';

interface EntryType {
  title: string;
  entity: Entity;
}

export const ProductInput = (props: {
  onChange: (name?: string) => void;
  team?: string;
  label?: string;
  helperText?: string;
  required?: boolean;
  fullWidth?: boolean;
  error?: boolean;
  id?: string;
  value?: string;
}) => {
  const catalogApi = useApi(catalogApiRef);

  const [entries, setEntries] = useState<EntryType[]>([]);
  const [initialValue, setInitialValue] = useState<EntryType | string>('');

  const { loading } = useAsync(async () => {
    if (props.team) {
      const catalogFilter = [
        { kind: ['Product'], 'spec.owner': props.team },
      ] as EntityFilterQuery;
      const entities = await catalogApi.getEntities(
        catalogFilter ? { filter: catalogFilter } : undefined,
      );
      if (entities?.items?.length > 0) {
        const items = entities.items
          .map(it => toEntry(it))
          .sort((a, b) => (a.title < b.title ? -1 : 1));
        let initialEntry = items.find(
          e => e.entity.metadata.name === props.value,
        );
        if (!initialEntry && props.value) {
          props.onChange(undefined);
        } else if (!initialEntry && items.length == 1) {
          initialEntry = items[0];
          props.onChange(items[0].entity.metadata.name);
        } else if (items.length == 0) {
          props.onChange(undefined);
        }
        setEntries(items);
        setInitialValue(initialEntry || '');
      } else {
        setEntries([]);
        setInitialValue('');
        props.onChange(undefined);
      }
    } else {
      setEntries([]);
      setInitialValue('');
      props.onChange(undefined);
    }
  }, [props.team, props.value]);

  const defaultProps = {
    options: entries,
    getOptionLabel: (option: EntryType) => (option?.title ? option.title : ''),
  };

  return (
    <FormControl
      margin="dense"
      required={props.required}
      fullWidth={props.fullWidth}
    >
      <Autocomplete
        {...defaultProps}
        id={props.id}
        value={initialValue}
        loading={loading}
        freeSolo
        renderInput={params => (
          <TextField
            {...params}
            label={props.label ? props.label : 'Product'}
            margin="dense"
            helperText={
              props.helperText ? props.helperText : 'Select a Product'
            }
            FormHelperTextProps={{ margin: 'dense', style: { marginLeft: 0 } }}
            variant="outlined"
            required={props.required}
            InputProps={params.InputProps}
            error={props.error}
          />
        )}
        onChange={(_, newValue) =>
          props.onChange((newValue as EntryType)?.entity?.metadata?.name)
        }
      />
    </FormControl>
  );

  function toEntry(entity: Entity): EntryType {
    return {
      title: `${entity.metadata.title} (${entity.metadata.annotations!['schema.dp.netrtl.com/vuid']})`,
      entity: entity,
    };
  }
};
